import analytics from 'storefront-analytics/dist/analytics'
import { init as initStorefrontAnalyticsLib } from 'storefront-analytics/dist/spa-analytics'
import type { Business, User } from '@pbt/pbt-ui-components'
import { Utils } from '@pbt/pbt-ui-components'

import { isPlaywrightTest } from './botDetection'
import { registerTrackingProvider } from './trackingProvider'

let isTrackingProviderRegistered = false

export function getIsStorefrontAnalyticsTrackingProviderRegistered() {
  return isTrackingProviderRegistered
}

export function setIsStorefrontAnalyticsTrackingProviderRegistered(
  value: boolean,
) {
  isTrackingProviderRegistered = value
}

interface StorefrontAnalyticsContext {
  akamaiBot?: string
  analyticsFeatureToggles?: Record<string, any>
  business?: Business
  experimentOverrides?: Record<string, any>
  isBot?: boolean
  userData?: User
  userRoles?: string[]
}

const getPageData = () => ({
  pageType: 'portal',
  'list-category': 'portal',
  app_version: process.env.REACT_APP_SENTRY_RELEASE || '0.0.0',
  ecomm_pagetype: 'portal',
  searchExperienceType: '',
  environment: Utils.getEnvironmentName(),
})

const getUserData = (
  user?: User,
  business?: Business,
  userRoles?: string[],
) => {
  if (!user) return undefined

  return {
    userId: user.id,
    userEmail: user.email,
    businessId: business?.id,
    businessType: business?.omniChannel ? 'CVC' : '3P',
    userRoles: userRoles?.join(', ') || '',
    firstName: user.firstName,
    lastName: user.lastName,
  }
}

export const initStorefrontAnalytics = (
  context: StorefrontAnalyticsContext = {},
) => {
  const {
    userData,
    business,
    userRoles,
    analyticsFeatureToggles = {},
  } = context

  const pageData = getPageData()
  const userContextData = getUserData(userData, business, userRoles)

  initStorefrontAnalyticsLib({
    pageData,
    userData: userContextData,
    analyticsFeatureToggles,
    isBot: isPlaywrightTest(), // Auto-detect Playwright if not explicitly provided
    akamaiBot: '',
  })

  // Register storefront-analytics as a tracking provider only once
  // According to the documentation (https://chewyinc.atlassian.net/wiki/spaces/SAT/pages/3137962092/How-to+Implement+events+with+storefront-analytics#Custom-Events),
  // custom events should call both trackEvent() and segmentTrack()
  if (!getIsStorefrontAnalyticsTrackingProviderRegistered()) {
    registerTrackingProvider({
      track: (eventName, meta = {}) => {
        const eventCategory = meta.eventCategory || 'Custom'
        const eventAction = meta.eventAction || eventName
        const eventLabel = meta.eventLabel || eventName
        const { eventValue } = meta

        if (window.chewy?.segmentContextualData?.analyticsInitialized) {
          analytics.trackEvent(
            eventCategory,
            eventAction,
            eventLabel,
            eventValue,
            meta,
          )

          analytics.segmentTrack(eventName, meta)
        }
      },
    })
    setIsStorefrontAnalyticsTrackingProviderRegistered(true)
  }
}
